import { useState, useEffect, useContext } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import {
  isPushSupported,
  getNotificationPermission,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush
} from '@/utils/pushNotifications';

export default function NotificationBell() {
  const { user } = useContext(AuthContext);
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [user]);

  const checkStatus = async () => {
    const isSupported = isPushSupported();
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(getNotificationPermission());
      const isSub = await isSubscribedToPush();
      setSubscribed(isSub);
      
      // Register service worker on load
      await registerServiceWorker();
    }
  };

  const handleToggleNotifications = async () => {
    if (!user) {
      toast.error('Please log in to enable notifications');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('authToken');

    try {
      if (subscribed) {
        // Unsubscribe
        const result = await unsubscribeFromPush(token);
        if (result.success) {
          setSubscribed(false);
          toast.success('Notifications disabled');
        } else {
          toast.error(result.message);
        }
      } else {
        // First request permission if needed
        if (permission !== 'granted') {
          const permResult = await requestNotificationPermission();
          setPermission(permResult.permission);
          
          if (!permResult.success) {
            toast.error(permResult.message);
            setLoading(false);
            return;
          }
        }
        
        // Subscribe
        const result = await subscribeToPush(token);
        if (result.success) {
          setSubscribed(true);
          toast.success("You'll get notified when we go live!");
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not supported
  if (!supported) {
    return null;
  }

  // Don't render if not logged in
  if (!user) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggleNotifications}
      disabled={loading || permission === 'denied'}
      className={`relative ${subscribed ? 'text-brand-lime' : 'text-gray-400 hover:text-white'}`}
      title={
        permission === 'denied' 
          ? 'Notifications blocked - enable in browser settings' 
          : subscribed 
            ? 'Notifications enabled - click to disable' 
            : 'Click to enable live stream notifications'
      }
      data-testid="notification-bell"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : subscribed ? (
        <BellRing className="w-5 h-5" />
      ) : permission === 'denied' ? (
        <BellOff className="w-5 h-5 text-red-500" />
      ) : (
        <Bell className="w-5 h-5" />
      )}
      {subscribed && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-brand-lime rounded-full" />
      )}
    </Button>
  );
}
